import { useEffect, useMemo, useRef, useState, useCallback, type CSSProperties } from 'react';
import { X, ChevronLeft, ChevronRight, Download, FileText, Sparkles, Loader2, Pencil, Maximize2, Minimize2, Volume2, VolumeX } from 'lucide-react';
import { useVideosMuted } from './slides/videoPrefs';
import { useFullscreen, fullscreenStageStyle } from './slides/useFullscreen';
import { coworkApi } from '../api/coworkApi';

interface PresentationModalProps {
  presentationId: string;
  onClose: () => void;
  /** When true, also offer the opt-in Gamma export button. */
  gammaEnabled?: boolean;
  /** Shown as an Edit button in the toolbar; opens the Reports editor. */
  onEdit?: (presentationId: string) => void;
  /** Open on this page (the editor passes the page being edited). */
  initialIndex?: number;
}

interface PresentationData {
  id: string;
  title: string;
  slides: any[];
  brand: any;
}

// ─── Theme & tokens (mirror of backend/src/engine/slideDesign.ts) ───────────

import { SlideRenderer, deriveTheme, type Theme } from './slides/SlideRenderer';
import { useDeckFonts } from './slides/deckFonts';
export { SlideRenderer, deriveTheme, type Theme };

// ─── Modal shell ────────────────────────────────────────────────────────────

export function PresentationModal({ presentationId, onClose, gammaEnabled, onEdit, initialIndex = 0 }: PresentationModalProps) {
  const [data, setData] = useState<PresentationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [index, setIndex] = useState(Math.max(0, initialIndex));
  const [exporting, setExporting] = useState<'pdf' | 'pptx-image' | 'pptx-editable' | 'gamma' | null>(null);
  const [pptxMenuOpen, setPptxMenuOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    coworkApi.presentations.get(presentationId)
      .then((d) => { if (!cancelled) { const pd = d as PresentationData; setData({ ...pd, slides: (pd.slides || []).filter((sl: any) => !sl?._plan) }); } })
      .catch((e: Error) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [presentationId]);

  const [videosMuted, setVideosMuted] = useVideosMuted();
  const theme = useMemo(() => deriveTheme(data?.brand ?? {}), [data?.brand]);
  useDeckFonts(data?.slides, (data?.brand as { fontFamily?: string } | undefined)?.fontFamily);
  const stageRef = useRef<HTMLDivElement>(null);
  const { isFs, toggle: toggleFs, supported: fsSupported } = useFullscreen(stageRef);
  const total = data?.slides.length ?? 0;

  const next = useCallback(() => setIndex((i) => Math.min(i + 1, Math.max(total - 1, 0))), [total]);
  const prev = useCallback(() => setIndex((i) => Math.max(i - 1, 0)), []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // A playing <video> owns its keys (space = pause, arrows = seek).
      if ((e.target as HTMLElement | null)?.tagName === 'VIDEO' && e.key !== 'Escape') return;
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, onClose]);

  async function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }

  async function handleExport(kind: 'pdf' | 'pptx-image' | 'pptx-editable' | 'gamma') {
    if (!data) return;
    setExporting(kind);
    setPptxMenuOpen(false);
    try {
      const safeTitle = data.title.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '-').slice(0, 60) || 'presentation';
      if (kind === 'pdf') {
        const blob = await coworkApi.presentations.exportPdf(data.id);
        await downloadBlob(blob, `${safeTitle}.pdf`);
      } else if (kind === 'pptx-image') {
        const blob = await coworkApi.presentations.exportPptx(data.id, 'pixel-perfect');
        await downloadBlob(blob, `${safeTitle}.pptx`);
      } else if (kind === 'pptx-editable') {
        const blob = await coworkApi.presentations.exportPptx(data.id, 'editable');
        await downloadBlob(blob, `${safeTitle}-editable.pptx`);
      } else if (kind === 'gamma') {
        const result = await coworkApi.presentations.exportGamma(data.id);
        window.open(result.documentUrl, '_blank');
      }
    } catch (e) {
      alert(`Export failed: ${(e as Error).message}`);
    } finally {
      setExporting(null);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(8, 8, 16, 0.88)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 1280, display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#fff' }}>
          <div style={{ fontSize: 14, opacity: 0.85, letterSpacing: '-0.005em' }}>
            {data?.title ?? 'Loading…'}{total > 0 ? ` — ${index + 1} / ${total}` : ''}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {onEdit && (
              <ToolbarButton onClick={() => data && onEdit(data.id)} disabled={!data}>
                <Pencil size={14} /> Edit
              </ToolbarButton>
            )}
            {fsSupported && (
              <ToolbarButton onClick={() => void toggleFs()} disabled={!data} title="Full screen (Esc to leave)">
                <Maximize2 size={14} /> Full screen
              </ToolbarButton>
            )}
            <ToolbarButton onClick={() => setVideosMuted(!videosMuted)} title={videosMuted ? 'Videos are muted on every page — click to unmute' : 'Mute all videos (remembered on this device)'}>
              {videosMuted ? <VolumeX size={14} /> : <Volume2 size={14} />} {videosMuted ? 'Muted' : 'Sound'}
            </ToolbarButton>
            <ToolbarButton onClick={() => handleExport('pdf')} disabled={!data || exporting !== null}>
              {exporting === 'pdf' ? <Loader2 size={14} className="spin" /> : <FileText size={14} />} PDF
            </ToolbarButton>
            <div style={{ position: 'relative' }}>
              <ToolbarButton
                onClick={() => setPptxMenuOpen((o) => !o)}
                disabled={!data || exporting !== null}
              >
                {exporting === 'pptx-image' || exporting === 'pptx-editable'
                  ? <Loader2 size={14} className="spin" />
                  : <Download size={14} />} PPTX ▾
              </ToolbarButton>
              {pptxMenuOpen && (
                <div
                  onMouseLeave={() => setPptxMenuOpen(false)}
                  style={{
                    position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 10,
                    background: '#0f172a', border: '1px solid rgba(255,255,255,0.14)',
                    borderRadius: 10, padding: 6, minWidth: 240,
                    boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
                  }}
                >
                  <PptxModeOption
                    label="Pixel-perfect"
                    description="Each slide as a high-res image. Visually identical to the viewer."
                    onClick={() => handleExport('pptx-image')}
                  />
                  <PptxModeOption
                    label="Editable text"
                    description="Real text shapes you can edit in PowerPoint / Keynote. Simpler design."
                    onClick={() => handleExport('pptx-editable')}
                  />
                </div>
              )}
            </div>
            {gammaEnabled && (
              <ToolbarButton onClick={() => handleExport('gamma')} disabled={!data || exporting !== null}>
                {exporting === 'gamma' ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />} Polish with Gamma
              </ToolbarButton>
            )}
            <ToolbarButton onClick={onClose} aria-label="Close">
              <X size={14} />
            </ToolbarButton>
          </div>
        </div>

        {/* Slide stage — 16:9, container-query for fluid type. Full screen shows this element alone: slide only, no dark chrome. */}
        <div ref={stageRef} style={isFs ? fullscreenStageStyle(theme.bg).outer : undefined}>
        <div
          style={{
            position: 'relative',
            background: theme.bg,
            borderRadius: isFs ? 0 : 16,
            ...(isFs ? fullscreenStageStyle(theme.bg).inner : {}),
            overflow: 'hidden',
            aspectRatio: '16 / 9',
            boxShadow: isFs ? 'none' : '0 30px 80px rgba(0,0,0,0.55), 0 8px 24px rgba(0,0,0,0.35)',
            // `size` (not `inline-size`) so cqh resolves to the slide frame's
            // height, not the viewport. With aspect-ratio: 16/9 the height is
            // derived from width, so size container queries work cleanly.
            containerType: 'size',
          } as CSSProperties}
        >
          {error && (
            <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#b91c1c' }}>
              {error}
            </div>
          )}
          {!data && !error && (
            <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: theme.textMuted }}>
              <Loader2 size={28} className="spin" />
            </div>
          )}
          {data && data.slides[index] && (
            <SlideRenderer slide={data.slides[index]} index={index} theme={theme} title={data.title} />
          )}

          {total > 1 && (
            <>
              <NavArrow side="left" onClick={prev} disabled={index === 0} />
              <NavArrow side="right" onClick={next} disabled={index >= total - 1} />
            </>
          )}
          {isFs && <button type="button" onClick={() => void toggleFs()} title="Leave full screen (Esc)" style={{ position: 'absolute', right: 12, top: 12, opacity: 0.35, background: 'rgba(0,0,0,0.35)', color: '#fff', border: 0, borderRadius: 8, padding: 6, cursor: 'pointer' }}><Minimize2 size={16} /></button>}
        </div>
        </div>

        {/* Dots */}
        {total > 1 && (
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', paddingTop: 4 }}>
            {data!.slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                aria-label={`Slide ${i + 1}`}
                style={{
                  width: i === index ? 24 : 8,
                  height: 8, borderRadius: 4, border: 'none',
                  background: i === index ? '#fff' : 'rgba(255,255,255,0.28)',
                  cursor: 'pointer', padding: 0, transition: 'all 150ms',
                }}
              />
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        .spin { animation: spin 1s linear infinite }
      `}</style>
    </div>
  );
}

function PptxModeOption({ label, description, onClick }: { label: string; description: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', textAlign: 'left', padding: '8px 10px', borderRadius: 6,
        background: 'transparent', color: '#fff', border: 'none', cursor: 'pointer',
        transition: 'background 120ms',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <div style={{ fontSize: 12, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2, lineHeight: 1.3 }}>{description}</div>
    </button>
  );
}

function ToolbarButton({ children, onClick, disabled, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      {...rest}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '7px 13px', borderRadius: 9, border: '1px solid rgba(255,255,255,0.14)',
        background: 'rgba(255,255,255,0.06)', color: '#fff',
        fontSize: 12, fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background 120ms',
      }}
    >
      {children}
    </button>
  );
}

function NavArrow({ side, onClick, disabled }: { side: 'left' | 'right'; onClick: () => void; disabled: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={side === 'left' ? 'Previous slide' : 'Next slide'}
      style={{
        position: 'absolute', top: '50%', transform: 'translateY(-50%)',
        [side]: 16,
        width: 44, height: 44, borderRadius: '50%',
        background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none',
        display: 'grid', placeItems: 'center', cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.25 : 0.85,
        backdropFilter: 'blur(6px)',
      } as CSSProperties}
    >
      {side === 'left' ? <ChevronLeft size={22} /> : <ChevronRight size={22} />}
    </button>
  );
}

// ─── Type primitives ────────────────────────────────────────────────────────
